/**
 * @graphql cc
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useState, type ReactNode, type FC, useEffect } from 'react';
import { defineMessages, type IntlShape, useIntl } from 'react-intl-next';
import gql from 'graphql-tag';
import { cssMap } from '@compiled/react';

import { Inline, Text } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import EyeOpenIcon from '@atlaskit/icon/core/eye-open';
import ClockIcon from '@atlaskit/icon/core/clock';
import { token } from '@atlaskit/tokens';
import { jsx } from '@atlaskit/css';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { FadeIn } from '@atlaskit/motion';

import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { withFlags, type WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { useContentType } from '@confluence/page-context';

import type { GeneralAccessLevel } from '../../../commonTypes.tsx';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../controller/Controller.tsx';

import type {
	PendingRequestExistsQuery as PendingRequestExistsQueryType,
	PendingRequestExistsQueryVariables,
} from './__types__/PendingRequestExistsQuery.ts';
import {
	type SubmitAccessRequestMutation as SubmitAccessRequestMutationType,
	type SubmitAccessRequestMutationVariables,
	AccessType,
} from './__types__/SubmitAccessRequestMutation.ts';

const i18n = defineMessages({
	noPermissionMessage: {
		id: 'share-and-restrict-dialog.ask-to-edit-box.no-permission-message',
		defaultMessage: 'You have view-only access to this content.',
		description: 'Message shown when user does not have permission to edit content in a space',
	},
	noPermissionSpaceRestrictedMessage: {
		id: 'share-and-restrict-dialog.ask-to-edit-box.no-permission.space-restricted-message',
		defaultMessage: 'You don’t have permission to edit content in this space.',
		description:
			'Message shown when user does not have permission to edit content in a space because of space restrictions',
	},
	askToEditButton: {
		id: 'share-and-restrict-dialog.ask-to-edit-box.ask-to-edit-button',
		defaultMessage: 'Ask to edit',
		description: 'Button text for requesting edit permissions',
	},
	requestSentMessage: {
		id: 'share-and-restrict-dialog.ask-to-edit-box.request-sent-message',
		defaultMessage: 'Edit requested',
		description: 'Confirmation message shown after edit request has been sent',
	},
	requestErrorMessage: {
		id: 'share-and-restrict-dialog.ask-to-edit-box.request-error-message',
		defaultMessage: 'We ran into a problem sending the request.',
		description: 'Error message show in a flag when submitting an error request fails.',
	},
});

const PendingRequestExistsQuery = gql`
	query AskToEditBoxPendingRequestExistsQuery(
		$accessRequestedAaid: String!
		$cloudId: ID!
		$contentId: ID!
	) {
		confluence_pendingRequestExists(
			accessRequestedAaid: $accessRequestedAaid
			cloudId: $cloudId
			contentId: $contentId
		) {
			isPendingAccessRequestExists
		}
	}
`;

const RequestPageAccessMutation = gql`
	mutation AskToEditBoxRequestPageAccessMutation($requestPageAccessInput: RequestPageAccessInput!) {
		requestPageAccess(requestPageAccessInput: $requestPageAccessInput) {
			displayName
		}
	}
`;

const styles = cssMap({
	container: {
		boxSizing: 'border-box',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.small'),
		minHeight: '48px',
		width: '100%',
		padding: token('space.150'),
		paddingLeft: token('space.100'),
		display: 'flex',
		alignItems: 'center',
	},
	buttonContainer: {
		flex: '0 0 auto',
		marginLeft: token('space.200'),
	},
	iconContainer: {
		width: '24px',
		height: '24px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

const IconContainer: FC<{ children: ReactNode }> = ({ children }) => (
	<div css={styles.iconContainer}>{children}</div>
);

export interface AskToEditBoxInternalProps {
	onClick: () => void;
	editRequested: boolean;
	isLoading: boolean;
	hasError: boolean;
	isLoadingForFirstTime: boolean;
	isRestrictedBySpace: boolean;
	intl: IntlShape;
}

export const AskToEditBoxPresentation: FC<AskToEditBoxInternalProps> = ({
	onClick,
	editRequested,
	isLoading,
	hasError,
	isLoadingForFirstTime,
	isRestrictedBySpace,
	intl,
}) => {
	const editRestrictionCopy = isRestrictedBySpace
		? i18n.noPermissionSpaceRestrictedMessage
		: i18n.noPermissionMessage;

	const shouldShowEditRequestedText = editRequested && !isLoading && !hasError;
	const shouldShowEditRequestedButton = !editRequested && !isLoadingForFirstTime && !hasError;
	const [animateFromRight, setAnimateFromRight] = useState(false);

	const handleClick = () => {
		setAnimateFromRight(true);
		onClick();
	};

	return (
		<div css={styles.container}>
			<Inline alignBlock="center" spread="space-between" grow="fill">
				<Inline alignBlock="center" space="space.150">
					<IconContainer>
						<EyeOpenIcon label="" color={token('color.icon.subtle')} />
					</IconContainer>
					<Text maxLines={6}>{intl.formatMessage(editRestrictionCopy)}</Text>
				</Inline>

				<div css={styles.buttonContainer}>
					{shouldShowEditRequestedText && (
						<FadeIn
							entranceDirection={animateFromRight ? 'right' : undefined}
							distance="proportional"
						>
							{({ ...props }) => (
								<div {...props}>
									<Inline alignBlock="center" space="space.050" grow="hug">
										<ClockIcon label="" color={token('color.icon.subtlest')} size="small" />
										<Text maxLines={4} color="color.text.subtlest" weight="medium">
											{intl.formatMessage(i18n.requestSentMessage)}
										</Text>
									</Inline>
								</div>
							)}
						</FadeIn>
					)}

					{shouldShowEditRequestedButton && (
						<FadeIn duration="large">
							{({ ...props }) => (
								<div {...props}>
									<Button spacing="compact" onClick={handleClick} isLoading={isLoading}>
										{intl.formatMessage(i18n.askToEditButton)}
									</Button>
								</div>
							)}
						</FadeIn>
					)}
				</div>
			</Inline>
		</div>
	);
};

export interface AskToEditBoxProps {
	contentId: string;
	stagedGeneralAccessLevel: GeneralAccessLevel;
}

const AskToEditBoxWithFlags: FC<AskToEditBoxProps & WithFlagsProps> = ({
	contentId,
	stagedGeneralAccessLevel,
	flags,
}) => {
	const { userId, cloudId } = useSessionData();
	const [hasUpdateError, setUpdateHasError] = useState(false);
	const [isSubmitRequestLoading, setIsSubmitRequestLoading] = useState(false);
	const [isLoadingForFirstTime, setIsLoadingForFirstTime] = useState(true);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [contentType] = useContentType();
	const isRestrictedBySpace = stagedGeneralAccessLevel === 'open-can-edit';
	const intl = useIntl();

	const {
		data,
		loading: queryLoading,
		error: queryError,
		refetch,
	} = useQueryInterop<PendingRequestExistsQueryType, PendingRequestExistsQueryVariables>(
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
		PendingRequestExistsQuery,
		{
			variables: {
				cloudId,
				accessRequestedAaid: userId ?? '',
				contentId,
			},
			fetchPolicy: 'network-only',
			onCompleted: () => {
				setIsLoadingForFirstTime(false);
			},
		},
	);

	const editRequested = Boolean(
		data?.confluence_pendingRequestExists?.isPendingAccessRequestExists,
	);

	const [submitAccessRequestMutation] = useMutationInterop<
		SubmitAccessRequestMutationType,
		SubmitAccessRequestMutationVariables
		/* @ts-expect-error -- http://go/cfe-use-relay-artifacts */
	>(RequestPageAccessMutation);

	const readyToFireShownEvent = !!data && !queryLoading && !queryError && isLoadingForFirstTime;
	useEffect(() => {
		if (readyToFireShownEvent) {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'viewed',
					actionSubject: 'infoMessage',
					actionSubjectId: 'askToEdit',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					attributes: {
						accessType: 'edit',
						contentId,
						contentType,
						isRestrictedBySpace,
						editRequested,
					},
				},
			}).fire();
		}
	}, [
		contentId,
		contentType,
		isRestrictedBySpace,
		editRequested,
		readyToFireShownEvent,
		createAnalyticsEvent,
	]);

	const onRequestEditAccessClick = useCallback(async () => {
		setIsSubmitRequestLoading(true);

		try {
			// This is fired before the mutation to preserve the pattern from the RestrictedPage and other components
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'requestAccessButton',
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					attributes: {
						accessType: 'edit',
						contentId,
						contentType,
						editDeniedButCanView: true,
					},
				},
			}).fire();

			await submitAccessRequestMutation({
				variables: {
					requestPageAccessInput: {
						accessType: AccessType.EDIT,
						pageId: contentId,
					},
				},
			});

			await refetch();
		} catch (e) {
			setUpdateHasError(true);
			void flags.showErrorFlag({
				id: 'share-and-restrict-dialog.ask-to-edit-box.submit-request.error',
				title: intl.formatMessage(i18n.requestErrorMessage),
			});
		}

		setIsSubmitRequestLoading(false);
	}, [
		submitAccessRequestMutation,
		contentId,
		contentType,
		refetch,
		flags,
		createAnalyticsEvent,
		intl,
	]);

	return (
		<AskToEditBoxPresentation
			editRequested={editRequested}
			onClick={onRequestEditAccessClick}
			isLoading={isSubmitRequestLoading || queryLoading}
			hasError={hasUpdateError || !!queryError}
			isLoadingForFirstTime={isLoadingForFirstTime}
			isRestrictedBySpace={isRestrictedBySpace}
			intl={intl}
		/>
	);
};

export const AskToEditBox = withFlags(AskToEditBoxWithFlags);
